// Global Error Handler for unhandled errors
import { captureError, captureMessage, isSafariMediaEmptyRangesNoise } from './SentryConfig';

function isBenignInvalidOrigin(reason) {
  const msg = reason && typeof reason === 'object' && 'message' in reason
    ? String(reason.message)
    : String(reason || '');
  return /^invalid origin\.?$/i.test(msg.trim());
}

export class GlobalErrorHandler {
  static initialize() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      // Extensions and in-app browsers dispatch synthetic `unhandledrejection` CustomEvents.
      // A real rejection is a PromiseRejectionEvent with a reason.
      const isRealRejection = event && 'promise' in event && event.promise;
      if (!isRealRejection || event.reason == null || (typeof Event !== 'undefined' && event.reason instanceof Event)) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        return;
      }
      if (isSafariMediaEmptyRangesNoise(event.reason)) {
        event.preventDefault();
        return;
      }
      // Stripe.js / some third-party SDKs reject with "invalid origin" when the domain
      // isn’t registered for the key (or strict mobile browsers) — don’t spam Sentry
      if (isBenignInvalidOrigin(event.reason)) {
        event.preventDefault();
        return;
      }

      // Unhandled promise rejection - handled by error tracking
      
      // Capture error in Sentry
      captureError(event.reason, {
        type: 'unhandled_promise_rejection',
        url: window.location.href
      });
      
      // Log to analytics if available
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('unhandled_promise_rejection', {
          reason: event.reason?.toString() || 'Unknown',
          stack: event.reason?.stack || '',
          url: window.location.href
        });
      }
      
      // Prevent default browser behavior
      event.preventDefault();
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      // Cross-origin "Script error." and resource load errors have no error object; Sentry's own handler covers those
      if (!event.error) {
        return;
      }
      if (isSafariMediaEmptyRangesNoise(event.message) || isSafariMediaEmptyRangesNoise(event.error)) {
        event.preventDefault();
        return;
      }
      // Uncaught error - handled by error tracking
      
      // Capture error in Sentry
      captureError(event.error, {
        type: 'uncaught_error',
        message: event.message || 'Unknown error',
        filename: event.filename || '',
        lineno: event.lineno || 0,
        url: window.location.href
      });
      
      // Log to analytics if available
      if (window.AnalyticsManager) {
        window.AnalyticsManager.trackEvent('uncaught_error', {
          message: event.message || 'Unknown error',
          filename: event.filename || '',
          lineno: event.lineno || 0,
          colno: event.colno || 0,
          stack: event.error?.stack || '',
          url: window.location.href
        });
      }
    });

    // Global error handler initialized
  }

  static handleError(error, context = '') {
    // Error in context - handled by error tracking
    
    // Log to analytics if available
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('handled_error', {
        context,
        message: error.message || 'Unknown error',
        stack: error.stack || '',
        url: window.location.href
      });
    }
  }
}